import {
  getCurrentUserType,
  IProduct,
  IShoppingCartResponse,
  LOGGED_USER_KEYS,
  retrieveCartCookie,
} from '@vfit/consumer/data-access';
import {
  CartProduct,
  ITrackError,
  ITrackErrorRes,
  NEXT_ERR_MSG,
  NEXT_ERR_TRANSACTION_ID,
  PageProduct,
  TEventName,
  TOptions,
  VisitorTrackingOpts,
} from '@vfit/shared/data-access';
import { removeCurrency } from '@vfit/consumer/hooks';
import { checkWindow, getFromLocalStorageByKey } from '@vfit/shared/data-access';
import { IStepCard } from '@vfit/shared/models';
import crypto from 'crypto-es';

// region UTILS

const organizeFlowsAppend = (
  flowCards: IStepCard[],
  enableAppend: boolean,
  idFromStep: string,
  appendSteps: IStepCard[]
) => {
  const indexAppend = flowCards.findIndex((el) => el.title === idFromStep);
  let organizedFlows = flowCards.slice(0, indexAppend + 1);
  const lastFlows = flowCards.slice(indexAppend + 1);
  if (enableAppend) {
    const alreadyPresent = appendSteps[0].title === lastFlows[0].title;
    if (alreadyPresent) return flowCards;
    organizedFlows = organizedFlows.concat(appendSteps);
    organizedFlows = organizedFlows.concat(lastFlows);
  } else if (lastFlows[0].title === appendSteps[0].title) {
    const removedAppendInfo = lastFlows.slice(appendSteps.length);
    organizedFlows = organizedFlows.concat(removedAppendInfo);
  } else if (appendSteps?.findIndex((aS) => aS.title === lastFlows[0].title) > -1) {
    const removedAppendInfo = lastFlows.slice(1);
    organizedFlows = organizedFlows.concat(removedAppendInfo);
  } else {
    organizedFlows = organizedFlows.concat(lastFlows);
  }
  return organizedFlows;
};

const addRemoveVisibilityCards = (type: 'hide' | 'show') => {
  const cardsEl: HTMLElement | null = document.getElementById('stepper-cards-container');
  const footerEl: HTMLElement | null = document.getElementById('sticky-offer-stepper');
  if (cardsEl && footerEl) {
    if (type === 'hide') {
      cardsEl.style.visibility = 'hidden';
      footerEl.style.visibility = 'hidden';
    } else {
      cardsEl.style.visibility = 'inherit';
      footerEl.style.visibility = 'inherit';
    }
  }
};

// endregion

// region TAGGING
const getEnergyTagging = (product: IProduct, pageType: string, type = 'mobile') => {
  const shoppingCart = getFromLocalStorageByKey('shoppingCart');
  const isCheckOrder = window?.location?.href?.includes('checkOrder');
  const isWinBack = product?.isWinback || product?.isWinbackLegacy;
  let cartWithAddons;
  const trackingProduct: CartProduct = {
    cart_id: (shoppingCart as IShoppingCartResponse)?.id,
    cart_product_id: `${product?.offerId}`,
    cart_product_name: product?.slug,
    cart_product_category: type,
    cart_product_price: product?.price && removeCurrency(product.price),
    cart_product_quantity: '1',
    cart_total: product?.price && removeCurrency(product.price),
    ...(cartWithAddons && cartWithAddons),
  };

  const opts: TOptions = {
    event_label: pageType,
    product_target_segment: 'consumer',
    event_category: 'sales',
    page_subsection: 'checkout',
    page_section: isWinBack ? 'campaign' : `${type} offers`,
    page_type: isCheckOrder ? 'order_loading' : pageType,
    journey_type: 'journey',
    journey_name: isWinBack ? 'winback' : 'checkout',
  };

  const pageProductsInfo: PageProduct = {
    product_name: product?.slug || '',
    product_price: product?.price ? removeCurrency(product?.price) : '',
    product_id: `${product?.offerId}`,
    product_quantity: '1',
    product_category: type,
  };

  return {
    trackingProduct,
    opts,
    pageProductsInfo,
  };
};

const getUserInfo = (): VisitorTrackingOpts => {
  const { SHA256 } = crypto;
  const user = getFromLocalStorageByKey('user');
  const silentLogin: any = getFromLocalStorageByKey('silentLogin');
  const loginEnrichement: any = getFromLocalStorageByKey('loginEnriched');
  const isLoggedUser = LOGGED_USER_KEYS.includes(silentLogin?.flowId) || false;
  const customerData: any = getFromLocalStorageByKey('customerData');
  const appSessionFromStorage: any = checkWindow() && localStorage.getItem('appSession');
  const appSession: any = !!appSessionFromStorage && JSON.parse(appSessionFromStorage);
  const appMsisdn: string = appSession?.value;
  const visitorLoginStatus = appMsisdn || isLoggedUser ? 'logged in' : 'logged out';

  return {
    visitor_login_type: 'web',
    ...(!!retrieveCartCookie() && { visitor_type: 'returning visitor' }),
    ...(user?.customer?.[0].id && { visitor_customer_id: user?.customer[0].id }),
    ...(user?.taggedInfoCust?.email && {
      visitor_id_email: SHA256(user?.taggedInfoCust?.email).toString(),
    }),
    ...(user?.customer?.[0].msisdns && {
      visitor_id_asset_list: user?.customer?.[0].msisdns?.map(
        (msisdn: string) => SHA256(msisdn)?.toString() || ''
      ),
    }),
    ...(loginEnrichement &&
      loginEnrichement.items[0]?.value && {
        visitor_id_asset_active: SHA256(loginEnrichement.items[0]?.value).toString(),
      }),
    ...(appMsisdn && { visitor_id_asset_active: SHA256(appMsisdn).toString() }),
    ...(visitorLoginStatus && { visitor_login_status: visitorLoginStatus }),
    ...(customerData &&
      customerData[0]?.owningIndividual?.birthDate && {
        visitor_customer_dob: customerData[0]?.owningIndividual?.birthDate,
      }),
    ...(customerData &&
      customerData[0]?.owningIndividual?.gender && {
        visitor_customer_gender: customerData[0]?.owningIndividual?.gender,
      }),
    ...(customerData &&
      customerData[0]?.billingAddress?.city && {
        visitor_customer_location_city: customerData[0]?.billingAddress?.city,
      }),
    ...(customerData &&
      customerData[0]?.owningIndividual?.nation && {
        visitor_customer_location_country: customerData[0]?.owningIndividual?.nation,
      }),
    ...(customerData &&
      customerData[0]?.billingAddress?.postalCode && {
        visitor_customer_location_id: customerData[0]?.billingAddress?.postalCode,
      }),
    ...(customerData &&
      customerData[0]?.owningIndividual?.firstName && {
        visitor_customer_name_first: customerData[0]?.owningIndividual?.firstName,
      }),
    ...(customerData &&
      customerData[0]?.owningIndividual?.lastName && {
        visitor_customer_name_last: customerData[0]?.owningIndividual?.lastName,
      }),
    ...(customerData &&
      customerData[0]?.billingAddress?.stateOrProvince && {
        visitor_customer_region: customerData[0]?.billingAddress?.stateOrProvince,
      }),
  };
};

// endregion

// region utils FLOWS

const getEnergyTrackError = (
  product: IProduct,
  error: ITrackError,
  step?: string
): ITrackErrorRes => {
  const pageError = [
    error?.error?.url || '',
    error?.error?.status || '',
    error?.errorType || '',
    error?.errorCode || '',
    error?.isBlocking ? 'blk' : 'nblk',
    step || '',
  ]
    .filter(Boolean)
    .join('_');
  const lastDexTransaction = localStorage?.getItem(NEXT_ERR_TRANSACTION_ID) || '';
  const lastErrorMessage = localStorage?.getItem(NEXT_ERR_MSG) || '';
  const transaction_id = error?.transaction_id || lastDexTransaction;
  const err_errorMessage = error?.err_errorMessage || lastErrorMessage;
  const cartProduct = getEnergyTagging(product, 'mobile').trackingProduct;
  const userType = getCurrentUserType();
  return {
    flow: 'mobile',
    event_name: 'page_error' as TEventName,
    event_label_track: 'page_error',
    event_category: 'error',
    page_error: pageError,
    page_error_code: error?.errorCode || '',
    page_type: 'error page',
    slug: product?.slug || '',
    isBlocking: error?.isBlocking ? 'Y' : 'N',
    step,
    ...(product?.offerId && { offerId: product?.offerId }),
    ...(cartProduct && cartProduct),
    ...(cartProduct?.cart_id && { cart_id: cartProduct?.cart_id }),
    ...(transaction_id && { transaction_id }),
    ...(error?.error?.status && { status: error?.error?.status }),
    ...(error?.error?.url && { service_name: error?.error?.url }),
    ...(userType && { user_type: userType }),
    ...(err_errorMessage && { err_errorMessage }),
    ...(error?.err_errorCode && { err_errorCode: error?.err_errorCode }),
    ...(error?.err_backendErrorMessage && {
      err_backendErrorMessage: error?.err_backendErrorMessage,
    }),
    ...(error?.err_backendErrorCode && { err_backendErrorCode: error?.err_backendErrorCode }),
    ...(error?.err_backendSource && { err_backendSource: error?.err_backendSource }),
    ...(error?.otherInfo && { otherInfo: error.otherInfo }),
  };
};

// endregion

export {
  organizeFlowsAppend,
  getEnergyTagging,
  addRemoveVisibilityCards,
  getEnergyTrackError,
  getUserInfo,
};
